import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Zap, CreditCard, AlertCircle, Loader2 } from 'lucide-react';
import { usePurchaseBoostPack, usePaymentMethods } from '@/hooks/useBilling';
import { usePricingConfig, calculatePriceFromTiers } from '@/hooks/usePricingConfig';
import { toast } from 'sonner';

export default function BoostPacksTab({ hasPaymentMethod }: { hasPaymentMethod?: boolean }) {
  const { data: pricingData, isLoading: isPricingLoading, error: pricingError } = usePricingConfig();
  const [credits, setCredits] = useState(150);
  const [isPreparingPayment, setIsPreparingPayment] = useState(false);
  const { mutate: purchaseBoostPack, isPending } = usePurchaseBoostPack();
  const { data: paymentMethods } = usePaymentMethods();
  
  const defaultPaymentMethod = paymentMethods?.savedMethods?.find(m => m.is_default);
  const firstPaymentMethod = paymentMethods?.savedMethods?.[0];
  const activePaymentMethod = defaultPaymentMethod || firstPaymentMethod;

  // Use pricing data or fallback to defaults
  const config = pricingData?.config || {
    minCredits: 35,
    maxCredits: 2500,
    creditStep: 5,
    currency: 'USD'
  };

  const tiers = pricingData?.tiers || [];
  const amount = calculatePriceFromTiers(credits, tiers);

  const handlePurchase = async () => {
    if (!activePaymentMethod?.stripe_payment_method_id) {
      toast.error('Please add a payment method first');
      return;
    }

    // Provide immediate feedback
    setIsPreparingPayment(true);
    toast.loading('Preparing payment...', { id: 'boost-purchase' });
    
    try {
      await new Promise(resolve => setTimeout(resolve, 800)); // Brief delay for UX
      
      purchaseBoostPack({
        credits,
        paymentMethodId: activePaymentMethod.stripe_payment_method_id,
        expectedPrice: amount,
      }, {
        onSuccess: () => {
          toast.dismiss('boost-purchase');
          setIsPreparingPayment(false);
        },
        onError: () => {
          toast.dismiss('boost-purchase');
          setIsPreparingPayment(false);
        }
      });
    } catch {
      toast.dismiss('boost-purchase');
      setIsPreparingPayment(false);
    }
  };

  const getPopularBadge = (creditAmount: number) => {
    const tier = tiers.find(t => t.credits === creditAmount);
    return tier?.badge || null;
  };

  // Get quick select options from tiers or use fallback
  const quickSelectOptions = tiers.length > 0 
    ? tiers.map(tier => ({
        credits: tier.credits,
        price: tier.price,
        badge: tier.badge
      }))
    : [
    { credits: 35, price: 25 },
    { credits: 150, price: 100 },
    { credits: 400, price: 250 },
    { credits: 1250, price: 750 }
  ];

  // Show loading state while fetching pricing
  if (isPricingLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Purchase Credit Boost Pack</CardTitle>
            <CardDescription>
              Loading pricing information...
            </CardDescription>
          </CardHeader>
          <CardContent className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show error state if pricing fails to load
  if (pricingError) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Purchase Credit Boost Pack</CardTitle>
            <CardDescription>
              Failed to load pricing information. Please try again.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Unable to fetch current pricing. Please refresh the page or contact support.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Purchase Credit Boost Pack</CardTitle>
          <CardDescription>
            Add credits to your account instantly. Credits never expire and roll over month to month.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {!hasPaymentMethod && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                You need to add a payment method before purchasing credits.
              </AlertDescription>
            </Alert>
          )}

          {/* Quick Select Options */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {quickSelectOptions.map((option) => {
              const badge = getPopularBadge(option.credits);
              return (
                <button
                  key={option.credits}
                  onClick={() => setCredits(option.credits)}
                  className={`relative p-4 rounded-lg border-2 transition-all ${
                    credits === option.credits 
                      ? 'border-primary bg-primary/5' 
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  {badge && (
                    <Badge className="absolute -top-2 -right-2 text-xs">
                      {badge}
                    </Badge>
                  )}
                  <div className="text-2xl font-bold">{option.credits}</div>
                  <div className="text-sm text-muted-foreground">credits</div>
                  <div className="text-sm font-medium mt-1">
                    ${option.price.toFixed(2)}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Custom Slider */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Custom Amount</label>
              <span className="text-sm text-muted-foreground">
                {config.minCredits} - {config.maxCredits} credits
              </span>
            </div>
            <Slider
              value={[credits]}
              onValueChange={(value) => setCredits(value[0])}
              min={config.minCredits}
              max={config.maxCredits}
              step={config.creditStep}
              className="w-full"
            />
            <div className="flex items-center justify-between text-sm">
              <span>{config.minCredits}</span>
              <span className="font-medium">{credits} credits</span>
              <span>{config.maxCredits}</span>
            </div>
          </div>
          
          {/* Purchase Summary */}
          <div className={`rounded-lg p-6 space-y-4 transition-all border-2 ${
            isPending || isPreparingPayment 
              ? 'bg-muted/50 opacity-75 border-primary/30 animate-pulse' 
              : 'bg-muted border-transparent'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-primary" />
                <span className="font-medium">Credit Boost Pack</span>
              </div>
              <Badge variant="secondary">{credits} credits</Badge>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Price per credit</span>
                <span>${(amount / credits).toFixed(4)}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Credits</span>
                <span>{credits}</span>
              </div>
              <div className="border-t pt-2">
                <div className="flex items-center justify-between">
                  <span className="font-medium">Total</span>
                  <span className="text-2xl font-bold">${amount.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {activePaymentMethod && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <CreditCard className="h-4 w-4" />
                <span>
                  Charged to {activePaymentMethod.card_brand} •••• {activePaymentMethod.card_last4}
                </span>
              </div>
            )}

            <Button 
              onClick={handlePurchase} 
              disabled={!activePaymentMethod?.stripe_payment_method_id || isPending || isPreparingPayment}
              className="w-full"
              size="lg"
            >
              {isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing Payment...
                </>
              ) : isPreparingPayment ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Preparing Payment...
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4 mr-2" />
                  Purchase {credits} Credits
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Benefits Card */}
      <Card>
        <CardHeader>
          <CardTitle>Why Purchase Credits?</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-3">
            <li className="flex items-start gap-2">
              <div className="rounded-full bg-primary/10 p-1 mt-0.5">
                <Zap className="h-3 w-3 text-primary" />
              </div>
              <div>
                <div className="font-medium">Never Expire</div>
                <div className="text-sm text-muted-foreground">
                  Credits roll over month to month - use them when you need them
                </div>
              </div>
            </li>
            <li className="flex items-start gap-2">
              <div className="rounded-full bg-primary/10 p-1 mt-0.5">
                <Zap className="h-3 w-3 text-primary" />
              </div>
              <div>
                <div className="font-medium">Instant Activation</div>
                <div className="text-sm text-muted-foreground">
                  Credits are available immediately after purchase
                </div>
              </div>
            </li>
            <li className="flex items-start gap-2">
              <div className="rounded-full bg-primary/10 p-1 mt-0.5">
                <Zap className="h-3 w-3 text-primary" />
              </div>
              <div>
                <div className="font-medium">Flexible Usage</div>
                <div className="text-sm text-muted-foreground">
                  Use for any type of call - inbound or outbound
                </div>
              </div>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
} 